import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useClickOutside } from "@/hooks/useClickOutside";
import { useSession } from "@/hooks/useSession";
import { switchOrganization } from "@/lib/organization";
import { cx } from "@/components/ui";
import { NetworkLogo, useLogoShape } from "@/components/NetworkLogo";

/**
 * En-tête de la barre latérale : logo + réseau courant. Cliquable quand on a accès à
 * plusieurs réseaux (ou au niveau plateforme) pour basculer de l'un à l'autre.
 */
export function NetworkSwitcher({ subtitle }: { subtitle: string }) {
  const { me } = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);
  const switchable = me.organizations.length > 1 || me.platformAdmin;
  const current = me.organizations.find((o) => o.id === me.organization?.id);
  const name = me.organization?.name ?? "Pilote";
  const wide = useLogoShape(me.organization?.logoUrl) === "wide";

  return (
    <div ref={ref} className="relative min-w-0 flex-1">
      <button
        type="button"
        disabled={!switchable}
        onClick={() => setOpen((o) => !o)}
        className={cx("flex w-full items-center gap-[10px] text-left", switchable && "cursor-pointer")}
      >
        {!wide && <NetworkLogo name={name} logoUrl={me.organization?.logoUrl} size={32} tone="light" className="!rounded-[8px]" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 text-[15px] font-semibold text-ink">
            {wide ? (
              // Logotype horizontal : il remplace le nom du réseau.
              <img src={me.organization!.logoUrl!} alt={name} className="mb-[2px] h-[40px] max-w-[160px] object-contain object-left" />
            ) : (
              <span className="truncate">{name}</span>
            )}
            {switchable && <span className="text-[11px] text-muted">▾</span>}
          </div>
          <div className="truncate text-[12px] text-muted" title={subtitle}>
            {current?.viaPlatform ? "Accès plateforme · " : ""}
            {subtitle}
          </div>
        </div>
      </button>
      {open && (
        <div className="absolute left-0 w-[220px] top-[46px] z-30 max-h-[60vh] overflow-auto rounded-card-sm bg-white p-2 shadow-menu">
          {me.platformAdmin && (
            <>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate("/reseaux");
                }}
                className="block w-full rounded-lg px-3 py-[9px] text-left text-[13px] font-bold text-accent hover:bg-page"
              >
                Tous les réseaux →
              </button>
              <div className="my-1 h-px bg-line" />
            </>
          )}
          <div className="px-3 pb-1 pt-1 text-[11px] font-bold uppercase text-muted-2">Réseaux</div>
          {me.organizations.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => void switchOrganization(o.id)}
              className={cx(
                "flex w-full items-center justify-between gap-2 rounded-lg px-3 py-[9px] text-left text-[13px] font-semibold hover:bg-page",
                o.id === me.organization?.id ? "text-accent" : "text-ink-2"
              )}
            >
              <span className="flex min-w-0 items-center gap-2">
                <NetworkLogo name={o.name} logoUrl={o.logoUrl} size={22} tone="light" className="!rounded-[6px] !p-[1px]" />
                <span className="truncate">{o.name}</span>
              </span>
              {o.id === me.organization?.id ? <span>✓</span> : o.viaPlatform ? <span className="text-[10.5px] font-semibold text-faint">plateforme</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
